// apps/web/src/lib/painel/home/formato.ts — Fase 17.8, Plano 03 (UI-SPEC Copywriting; números, moeda e tempo em pt-BR).
// Valores monetários entram como string decimal (contrato) e são convertidos só para exibição.

const formatosPorCasas = new Map<number, Intl.NumberFormat>();

function formatoComCasas(casas: number): Intl.NumberFormat {
  let formato = formatosPorCasas.get(casas);
  if (formato === undefined) {
    formato = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: casas });
    formatosPorCasas.set(casas, formato);
  }
  return formato;
}

/** '12.345', '38,6'. Zero negativo (por arredondamento) sai como '0'. */
export function formatarNumero(valor: number, casas = 0): string {
  if (!Number.isFinite(valor)) return '—';
  const arredondado = Number(valor.toFixed(casas));
  return formatoComCasas(casas).format(arredondado === 0 ? 0 : arredondado);
}

/** 'R$ 58,4 mil', 'R$ 316 mil', 'R$ 1,2 mi'; abaixo de mil mostra reais inteiros. Espaço comum entre símbolo e número. */
export function formatarMoedaCompacta(valorDecimal: string): string {
  const valor = Number(valorDecimal);
  if (!Number.isFinite(valor)) return '—';
  const sinal = valor < 0 ? '-' : '';
  const abs = Math.abs(valor);
  if (abs >= 1_000) {
    const emMil = Math.round(abs / 100) / 10;
    if (abs < 1_000_000 && emMil < 1_000) return `${sinal}R$ ${formatarNumero(emMil, 1)} mil`;
    return `${sinal}R$ ${formatarNumero(abs / 1_000_000, 1)} mi`;
  }
  return `${sinal}R$ ${formatarNumero(abs, 0)}`;
}

/** 'R$ 38.420' (reais inteiros, espaço comum). */
export function formatarMoeda(valorDecimal: string): string {
  const valor = Number(valorDecimal);
  if (!Number.isFinite(valor)) return '—';
  const arredondado = Math.round(Math.abs(valor));
  return `${valor < 0 && arredondado !== 0 ? '-' : ''}R$ ${formatarNumero(arredondado, 0)}`;
}

/** '+3 pp', '-0,4 pp', '0 pp' (uma casa no máximo; variação que arredonda para zero vira '0 pp'). */
export function formatarPontosPercentuais(variacao: number): string {
  if (!Number.isFinite(variacao)) return '—';
  const arredondada = Math.round(variacao * 10) / 10;
  if (arredondada === 0) return '0 pp';
  return `${arredondada > 0 ? '+' : '-'}${formatarNumero(Math.abs(arredondada), 1)} pp`;
}

const EXTENSO_MASCULINO = ['Um', 'Dois', 'Três', 'Quatro', 'Cinco', 'Seis', 'Sete', 'Oito', 'Nove', 'Dez'] as const;
const EXTENSO_FEMININO = ['Uma', 'Duas', 'Três', 'Quatro', 'Cinco', 'Seis', 'Sete', 'Oito', 'Nove', 'Dez'] as const;

/** 1 a 10 por extenso com inicial maiúscula (início de frase); zero e 11 em diante em algarismos. */
export function porExtenso(n: number, genero: 'm' | 'f'): string {
  if (Number.isInteger(n) && n >= 1 && n <= 10) {
    return (genero === 'f' ? EXTENSO_FEMININO : EXTENSO_MASCULINO)[n - 1] ?? String(n);
  }
  return formatarNumero(n, 0);
}

/** 'agora', 'há 12 s', 'há 3 min', 'há 1 h', 'há 2 dias'. Menos de 10 s (ou relógio adiantado) é 'agora'. */
export function tempoRelativo(desde: Date, agora: Date): string {
  const segundos = Math.floor((agora.getTime() - desde.getTime()) / 1000);
  if (segundos < 10) return 'agora';
  if (segundos < 60) return `há ${segundos} s`;
  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas} h`;
  const dias = Math.floor(horas / 24);
  return `há ${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}

const FORMATO_HORA_SP = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'America/Sao_Paulo' });

/** '14:02' (hora e minuto de São Paulo) a partir de um instante ISO em UTC; instante ilegível não vira hora. */
export function horaSaoPaulo(iso: string): string | undefined {
  const instante = Date.parse(iso);
  return Number.isNaN(instante) ? undefined : FORMATO_HORA_SP.format(new Date(instante));
}

const FORMATO_DIA_SP = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'America/Sao_Paulo' });

/** 'AAAA-MM-DD' do dia civil de São Paulo em que o instante cai (virada às 00:00 de lá, não às 00:00 UTC). */
export function diaSaoPaulo(instante: Date): string {
  return FORMATO_DIA_SP.format(instante);
}

/** 'DD/MM' do dia civil de São Paulo ('26/08'); instante ilegível não vira data. */
export function diaMesSaoPaulo(iso: string): string | undefined {
  const instante = Date.parse(iso);
  if (Number.isNaN(instante)) return undefined;
  const [, mes, dia] = diaSaoPaulo(new Date(instante)).split('-');
  return `${dia}/${mes}`;
}

/** Quantos dias civis (em São Paulo) separam `desde` de `agora`: 0 = mesmo dia, 1 = ontem; instante no futuro vale 0. */
export function diasCivisAtras(desde: Date, agora: Date): number {
  const dia = (d: Date): number => {
    const [ano, mes, diaDoMes] = diaSaoPaulo(d).split('-').map(Number);
    return Date.UTC(ano ?? 0, (mes ?? 1) - 1, diaDoMes ?? 1) / 86_400_000;
  };
  return Math.max(0, dia(agora) - dia(desde));
}

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'] as const;

/** 'outubro' a partir de 'AAAA-MM'; mês inválido devolve o próprio texto. */
export function nomeDoMes(aaaaMm: string): string {
  const mes = Number(aaaaMm.slice(5, 7));
  return MESES[mes - 1] ?? aaaaMm;
}

/** 'AAAA-MM' do mês anterior ('2027-01' -> '2026-12'). */
export function mesAnterior(aaaaMm: string): string {
  const ano = Number(aaaaMm.slice(0, 4));
  const mes = Number(aaaaMm.slice(5, 7));
  return mes <= 1 ? `${ano - 1}-12` : `${ano}-${String(mes - 1).padStart(2, '0')}`;
}
